import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Key, AlertTriangle, Trophy, ClipboardCopy, ListChecks, Sparkles } from "lucide-react";

const mdComponents = {
  h1: ({ node, ...p }) => <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-10 mb-5 text-zinc-900" {...p} />,
  h2: ({ node, ...p }) => <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-10 mb-4 text-zinc-900 border-b border-zinc-200 pb-2" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-display font-semibold text-xl mt-7 mb-3 text-zinc-800" {...p} />,
  h4: ({ node, ...p }) => <h4 className="font-semibold text-lg mt-5 mb-2 text-zinc-800" {...p} />,
  p: ({ node, ...p }) => <p className="text-[15px] leading-relaxed text-zinc-700 mb-4" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-6 space-y-1.5 mb-4 text-zinc-700" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-6 space-y-1.5 mb-4 text-zinc-700" {...p} />,
  li: ({ node, ...p }) => <li className="text-[15px] leading-relaxed" {...p} />,
  a: ({ node, ...p }) => <a className="text-[#4F46E5] underline underline-offset-2 hover:text-[#4338CA]" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-zinc-900" {...p} />,
  blockquote: ({ node, ...p }) => <blockquote className="border-l-4 border-[#4F46E5] pl-4 italic text-zinc-600 my-4" {...p} />,
  code: ({ node, inline, ...p }) =>
    inline ? (
      <code className="bg-zinc-100 text-[#4F46E5] px-1.5 py-0.5 text-sm font-mono" {...p} />
    ) : (
      <code className="block bg-zinc-900 text-zinc-100 p-4 text-sm font-mono overflow-x-auto my-4" {...p} />
    ),
  table: ({ node, ...p }) => (
    <div className="overflow-x-auto my-5">
      <table className="w-full text-sm border border-zinc-200" {...p} />
    </div>
  ),
  th: ({ node, ...p }) => <th className="bg-zinc-50 border border-zinc-200 p-2.5 text-left font-semibold" {...p} />,
  td: ({ node, ...p }) => <td className="border border-zinc-200 p-2.5 align-top" {...p} />,
  hr: () => <hr className="my-8 border-zinc-200" />,
};

const Md = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
    {children}
  </ReactMarkdown>
);

const boxStyle = (title) => {
  const t = (title || "").toLowerCase();
  if (t.includes("alerta") || t.includes("error") || t.includes("fuga"))
    return { icon: AlertTriangle, cls: "border-l-[#FF3B30] bg-red-50", chip: "bg-[#FF3B30] text-white" };
  if (t.includes("caso") || t.includes("estudio"))
    return { icon: Trophy, cls: "border-l-[#10B981] bg-emerald-50", chip: "bg-[#10B981] text-white" };
  if (t.includes("swipe") || t.includes("script") || t.includes("plantilla"))
    return { icon: ClipboardCopy, cls: "border-l-[#F59E0B] bg-amber-50", chip: "bg-[#F59E0B] text-white" };
  return { icon: Key, cls: "border-l-[#4F46E5] bg-indigo-50", chip: "bg-[#4F46E5] text-white" };
};

const Box = ({ title, children }) => {
  const { icon: Icon, cls, chip } = boxStyle(title);
  return (
    <div className={`my-6 border border-zinc-200 border-l-4 ${cls} p-5`} data-testid="infoproduct-box">
      <div className="flex items-center gap-2 mb-3">
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 py-1 ${chip}`}>
          <Icon className="w-3.5 h-3.5" /> {title}
        </span>
      </div>
      <div className="[&>p:last-child]:mb-0">
        <Md>{children}</Md>
      </div>
    </div>
  );
};

const Worksheet = ({ children }) => (
  <div className="my-6 border-2 border-dashed border-[#4F46E5]/40 bg-indigo-50/40 p-5" data-testid="infoproduct-worksheet">
    <div className="flex items-center gap-2 mb-3">
      <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 py-1 bg-zinc-900 text-white">
        <ListChecks className="w-3.5 h-3.5" /> Hoja de trabajo
      </span>
    </div>
    <div className="[&_input]:mr-2 [&>p:last-child]:mb-0">
      <Md>{children}</Md>
    </div>
  </div>
);

const BLOCK_RE = /\[BOX:\s*([^\]]+)\]([\s\S]*?)\[\/BOX\]|\[WORKSHEET\]([\s\S]*?)\[\/WORKSHEET\]/g;

const InfoproductRenderer = ({ content = "" }) => {
  const parts = [];
  let last = 0;
  let m;
  let key = 0;
  BLOCK_RE.lastIndex = 0;
  while ((m = BLOCK_RE.exec(content)) !== null) {
    if (m.index > last) {
      parts.push(<Md key={`md-${key++}`}>{content.slice(last, m.index)}</Md>);
    }
    if (m[1] !== undefined) {
      parts.push(<Box key={`box-${key++}`} title={m[1].trim()}>{(m[2] || "").trim()}</Box>);
    } else {
      parts.push(<Worksheet key={`ws-${key++}`}>{(m[3] || "").trim()}</Worksheet>);
    }
    last = BLOCK_RE.lastIndex;
  }
  if (last < content.length) {
    parts.push(<Md key={`md-${key++}`}>{content.slice(last)}</Md>);
  }

  return (
    <div data-testid="infoproduct-content" className="max-w-none">
      {parts.length ? parts : (
        <div className="flex items-center gap-2 text-zinc-400 text-sm">
          <Sparkles className="w-4 h-4" /> Sin contenido todavía.
        </div>
      )}
    </div>
  );
};

export default InfoproductRenderer;
